import { call, put, takeLatest, select } from "redux-saga/effects";
import { toast } from "react-toastify";
import { push } from "../router/routerSlice";
import { decrypt } from "../../utils";
import { getUser } from "../../services/apiUrl";
import {
  getHealthParamCacheKey,
  isCacheValid,
  CACHE_CONFIGS,
  generateCacheKey
} from '../../utils/cacheUtils';
import {
  setCacheEntry,
  updateCacheStats,
  selectCacheEntry
} from '../cache/cacheSlice';

import {
  bloodOxygenAPI,
  bloodPressureAPI,
  bloodSugarAPI,
  bodyTempAPI,
  deleteHealthParamsAPI,
  getAllergy,
  getHealthCondition,
  getInvoiceDetailApi,
  getInvoiceListApi,
  getMedicalDetailByDoctorAPI,
  getMedicalDetailByPatientAPI,
  getMedicalHistoryAPI,
  getMedicalReport,
  getMedication,
  getPatientEprescriptionApi,
  getPatientPrescriptionApi,
  getSurgury,
  getVaccinations,
  healthParamsAPI,
  heartRateAPI,
  lipidProfileApi,
  qrcodeAPI,
  respiratoryRateAPI,
  sleepDataAPI,
  stepsDataAPI
} from "../../services/mainService";
import {
  getBloodSugarSuccess,
  getBloodSugarFailure,
  getHealthParamSuccess,
  getHealthParamFailure,
  getLipidSuccess,
  getLipidFailure,
  getPatientProfileSuccess,
  getPatientProfileFailure,
  getHeartRateFailure,
  getRespiratoryRateFailure,
  getRespiratoryRateSuccess,
  getHeartRateSuccess,
  getBodyTempSuccess,
  getBodyTempFailure,
  getBloodOxygenSuccess,
  getBloodOxygenFailure,
  getBloodPressureSuccess,
  getBloodPressureFailure,
  getMedicalHistoryRequest,
  getMedicalHistoryFailure,
  getMedicalHistorySuccess,
  getHealthConditionsSuccess,
  getHealthConditionsFailure,
  getMedicationSuccess,
  getMedicationsFailure,
  getSurgurySuccess,
  getSurguryFailure,
  getAllergySuccess,
  getAllergyFailure,
  getVaccinationSuccess,
  getVaccinationFailure,
  getQRcodeDetailsSuccess,
  getQRcodeDetailsFailure,
  getPatientInvoiceDetailSuccess,
  getPatientInvoiceDetailFailure,
  getPatientEprescriptionSuccess,
  getPatientEprescriptionFailure,
  getPatientEprescriptionRequest,
  getPatientMedicalReportRequest,
  getPatientMedicalReportSuccess,
  getPatientMedicalReportFailure,
  getInvoiceListSuccess,
  getInvoiceListFailure,
  getPatientInvoiceDetailRequest,
  getInvoiceListRequest,
  getStepsDataRequest,
    getStepsDataSuccess,
  getStepsDataFailure,
  getSleepDataSuccess,
  getSleepDataFailure,
} from "./userSlice";
import {
  bloodSugar,
  bloodOxygen,
  bloodPressure,
  bodyTemp,
  heartRate,
  respiratoryRate,
  healthParams,
  lipidProfile,
  patientProfile,
  healthConditions,
  medications,
  surguries,
  allergies,
  vaccination,
  qrCodeAction,
  patientInvoiceDetail,
  getPatientEprescriptionAction,
  getPatientMedicalReportAction,
  getMedicalHistoryAction,
  patientInvoiceListAction,
    deleteHealthParams,
  deleteHealthParamsSuccess,
  deleteHealthParamsFailure,
  getStepsData,
  getSleepData
} from "./userActions";
import { LipidProfileList } from "../../constant/staticLists";
import { getProfile } from "../../services/authService";

const ENCRYPTED_FIELDS_MAP = {
  bloodPressureData: ['sys_blood_pressure', 'dia_blood_pressure'],
  bloodSugarData: ['blood_sugar'],
  spo2sData: ['percentage'],
  bodyTemperatureData: ['temperature'],
  heartRateData: ['pulse'],
  respiratorieData: ['respiratory_rates'],
};

const PARAM_TYPE_TO_FIELDS = {
  bloodOxygen: ENCRYPTED_FIELDS_MAP.spo2sData,
  bodyTemp: ENCRYPTED_FIELDS_MAP.bodyTemperatureData,
  heartRate: ENCRYPTED_FIELDS_MAP.heartRateData,
  respiratoryRate: ENCRYPTED_FIELDS_MAP.respiratorieData,
};

const decryptRecords = (records, fields) => {
  if (!Array.isArray(records)) return records;
  return records.map(record => {
    const updated = { ...record };
    fields.forEach(field => {
      if (typeof updated[field] === 'string' && updated[field].length > 0) {
        try {
          updated[field] = decrypt(updated[field]);
        } catch (e) {
          // leave value as-is if decryption fails
        }
      }
    });
    return updated;
  });
};

const decryptHealthParamData = (data) => {
  if (!Array.isArray(data)) return data;
  return data.map(item => {
    const result = { ...item };
    Object.entries(ENCRYPTED_FIELDS_MAP).forEach(([key, fields]) => {
      if (Array.isArray(result[key])) {
        result[key] = decryptRecords(result[key], fields);
      }
    });
    return result;
  });
};


/**
 * Cache-aware saga for blood sugar API calls
 */
function* bloodSugarSaga(action) {
  try {
    // Generate cache key
    const cacheKey = getHealthParamCacheKey('bloodSugar', action.payload);
    
    // Check cache first
    const state = yield select();
    const cachedEntry = selectCacheEntry(state, 'healthParams', cacheKey);
    
    if (cachedEntry && isCacheValid(cachedEntry, CACHE_CONFIGS.HEALTH_PARAMS.expiry)) {
      // Cache hit
      yield put(updateCacheStats({ type: 'hit' }));
      yield put(getBloodSugarSuccess(cachedEntry.data));
      return;
    }
    
    // Cache miss - make API call
    yield put(updateCacheStats({ type: 'miss' }));
    const apiResponse = yield call(bloodSugarAPI, action.payload);
    
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        let userData = apiResponse.response.data;
        if (getUser() === 'doctor') {
          userData = decryptRecords(userData, ENCRYPTED_FIELDS_MAP.bloodSugarData);
        }

        // Cache the response
        yield put(setCacheEntry({
          cacheType: 'healthParams',
          key: cacheKey,
          data: userData
        }));

        yield put(getBloodSugarSuccess(userData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getBloodSugarFailure(apiResponse.response.error));
      }
    }
  } catch (error) {
    console.log("Error:", error);
    yield put(getBloodSugarFailure(error.message));
  }
}

/**
 * Cache-aware saga for health parameters API calls
 */
function* healthParamsSaga(action) {
  try {
    // Generate cache key
    const cacheKey = getHealthParamCacheKey('healthParams', action.payload);

    // Skip cache when explicitly bypassed (e.g. SSE-triggered refresh)
    if (!action.payload?.bypassCache) {
      // Check cache first
      const state = yield select();
      const cachedEntry = selectCacheEntry(state, 'healthParams', cacheKey);

      if (cachedEntry && isCacheValid(cachedEntry, CACHE_CONFIGS.HEALTH_PARAMS.expiry)) {
        // Cache hit
        yield put(updateCacheStats({ type: 'hit' }));
        yield put(getHealthParamSuccess(cachedEntry.data));
        return;
      }
    }
    
    // Cache miss - make API call
    yield put(updateCacheStats({ type: 'miss' }));
    const apiResponse = yield call(healthParamsAPI, action.payload);
    
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        let userData = apiResponse.response.data;
        if (getUser() === 'doctor') {
          userData = decryptHealthParamData(userData);
        }

        // Cache the response
        yield put(setCacheEntry({
          cacheType: 'healthParams',
          key: cacheKey,
          data: userData
        }));

        yield put(getHealthParamSuccess(userData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getHealthParamFailure(apiResponse.response.error));
      }
    }
  } catch (error) {
    console.log("Error:", error);
    yield put(getHealthParamFailure(error.message));
  }
}

/**
 * Cache-aware saga for blood pressure API calls
 */
function* bloodPressureSaga(action) {
  try {
    // Generate cache key
    const cacheKey = getHealthParamCacheKey('bloodPressure', action.payload);
    
    // Check cache first
    const state = yield select();
    const cachedEntry = selectCacheEntry(state, 'healthParams', cacheKey);
    
    if (cachedEntry && isCacheValid(cachedEntry, CACHE_CONFIGS.HEALTH_PARAMS.expiry)) {
      // Cache hit
      yield put(updateCacheStats({ type: 'hit' }));
      yield put(getBloodPressureSuccess(cachedEntry.data));
      return;
    }
    
    // Cache miss - make API call
    yield put(updateCacheStats({ type: 'miss' }));
    const apiResponse = yield call(bloodPressureAPI, action.payload);
    
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        let userData = apiResponse.response.data;
        if (getUser() === 'doctor') {
          userData = decryptRecords(userData, ENCRYPTED_FIELDS_MAP.bloodPressureData);
        }

        // Cache the response
        yield put(setCacheEntry({
          cacheType: 'healthParams',
          key: cacheKey,
          data: userData
        }));

        yield put(getBloodPressureSuccess(userData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getBloodPressureFailure(apiResponse.response.error));
      }
    }
  } catch (error) {
    console.log("Error:", error);
    yield put(getBloodPressureFailure(error.message));
  }
}

/**
 * Create cache-aware saga for other health parameters
 */
const createHealthParamSaga = (apiCall, successAction, failureAction, paramType) => {
  return function* (action) {
    try {
      // Generate cache key
      const cacheKey = getHealthParamCacheKey(paramType, action.payload);
      
      // Check cache first
      const state = yield select();
      const cachedEntry = selectCacheEntry(state, 'healthParams', cacheKey);
      
      if (cachedEntry && isCacheValid(cachedEntry, CACHE_CONFIGS.HEALTH_PARAMS.expiry)) {
        // Cache hit
        yield put(updateCacheStats({ type: 'hit' }));
        yield put(successAction(cachedEntry.data));
        return;
      }
      
      // Cache miss - make API call
      yield put(updateCacheStats({ type: 'miss' }));
      const apiResponse = yield call(apiCall, action.payload);
      
      if (Object.values(apiResponse).length === 0) {
        // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
      } else {
        if (apiResponse.response_type === "success") {
          let userData = apiResponse.response.data;
          const encryptedFields = PARAM_TYPE_TO_FIELDS[paramType];
          if (getUser() === 'doctor' && encryptedFields) {
            userData = decryptRecords(userData, encryptedFields);
          }

          // Cache the response
          yield put(setCacheEntry({
            cacheType: 'healthParams',
            key: cacheKey,
            data: userData
          }));

          yield put(successAction(userData));
        } else {
          toast.error(apiResponse.response.message, {
            position: "bottom-center",
            closeButton: false,
            autoClose: 1000,
            style: { width: "auto", borderRadius: 8 },
          });
          yield put(failureAction(apiResponse.response.error));
        }
      }
    } catch (error) {
      console.log("Error:", error);
      yield put(failureAction(error.message));
    }
  };
};

// Create cache-aware sagas for all health parameters
const bloodOxygenSaga = createHealthParamSaga(
  bloodOxygenAPI, 
  getBloodOxygenSuccess, 
  getBloodOxygenFailure, 
  'bloodOxygen'
);

const bodyTempSaga = createHealthParamSaga(
  bodyTempAPI, 
  getBodyTempSuccess, 
  getBodyTempFailure, 
  'bodyTemp'
);

const heartRateSaga = createHealthParamSaga(
  heartRateAPI, 
  getHeartRateSuccess, 
  getHeartRateFailure, 
  'heartRate'
);

const respiratoryRateSaga = createHealthParamSaga(
  respiratoryRateAPI, 
  getRespiratoryRateSuccess, 
  getRespiratoryRateFailure, 
  'respiratoryRate'
);

const stepsDataSaga = createHealthParamSaga(
  stepsDataAPI, 
  getStepsDataSuccess, 
  getStepsDataFailure, 
  'stepsData'
);

const sleepDataSaga = createHealthParamSaga(
  sleepDataAPI,
  getSleepDataSuccess,
  getSleepDataFailure,
  'sleepData'
);

/**
 * Cache-aware saga for lipid profile
 */
function* lipidProfileSaga(action) {
  try {
    // Generate cache key
    const userType = localStorage.getItem("userType") || 'patient';
    const cacheKey = generateCacheKey('lipidProfile', { 
      userType,
      duration: action.payload.duration,
      date: action.payload.date
    });
    
    // Check cache first
    const state = yield select();
    const cachedEntry = selectCacheEntry(state, 'lipidProfile', cacheKey);
    
    if (cachedEntry && isCacheValid(cachedEntry, CACHE_CONFIGS.LIPID_PROFILE.expiry)) {
      // Cache hit
      yield put(updateCacheStats({ type: 'hit' }));
      yield put(getLipidSuccess(cachedEntry.data));
      return;
    }
    
    // Cache miss - make API call
    yield put(updateCacheStats({ type: 'miss' }));
    const apiResponse = yield call(lipidProfileApi, action.payload);
    
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const { data } = apiResponse.response;
        const length = Array.isArray(data) && data.length;
        const payload = JSON.parse(JSON.stringify(LipidProfileList)).map(
          (x) => {
            if (length && length > 0) {
              x.value = data?.[length > 0 ? length - 1 : 0]?.[x.key] || 0;
            } else {
              x.value = data?.[x.key] || 0;
            }
            return x;
          }
        );
        
        // Cache the response
        yield put(setCacheEntry({
          cacheType: 'lipidProfile',
          key: cacheKey,
          data: payload
        }));
        
        yield put(getLipidSuccess(payload));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getLipidFailure(apiResponse.response.error));
      }
    }
  } catch (error) {
    console.log("Error:", error);
    yield put(getLipidFailure(error.message));
  }
}

// Keep original non-cached sagas for other functionalities
function* getProfileSaga(action) {
  try {
    const apiResponse = yield call(getProfile, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const userData = apiResponse.response.data;
        yield put(getPatientProfileSuccess(userData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getPatientProfileFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

// Keep all other saga functions unchanged for now
function* getHealthConditions(action) {
  try {
    const apiResponse = yield call(getHealthCondition, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const medicalData = apiResponse.response.data;
        yield put(getHealthConditionsSuccess(medicalData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getHealthConditionsFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getMedications(action) {
  try {
    const apiResponse = yield call(getMedication, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const medicalData = apiResponse.response.data;
        yield put(getMedicationSuccess(medicalData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getMedicationsFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getSurguries(action) {
  try {
    const apiResponse = yield call(getSurgury, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const medicalData = apiResponse.response.data;
        yield put(getSurgurySuccess(medicalData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getSurguryFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getAllergies(action) {
  try {
    const apiResponse = yield call(getAllergy, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const medicalData = apiResponse.response.data;
        yield put(getAllergySuccess(medicalData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getAllergyFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getVaccination(action) {
  try {
    const apiResponse = yield call(getVaccinations, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const medicalData = apiResponse.response.data;
        yield put(getVaccinationSuccess(medicalData));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getVaccinationFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getQRcode(action) {
  try {
    const apiResponse = yield call(qrcodeAPI, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        yield put(getQRcodeDetailsSuccess(data));
        yield put(push(`/patient/patient-info/${data}`));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getQRcodeDetailsFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getInvoiceList(action) {
  try {
    yield put(getInvoiceListRequest());
    const apiResponse = yield call(getInvoiceListApi, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        yield put(getInvoiceListSuccess(data));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getInvoiceListFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getPatientInvoiceDetail(action) {
  try {
    yield put(getPatientInvoiceDetailRequest());
    const apiResponse = yield call(getInvoiceDetailApi, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        yield put(getPatientInvoiceDetailSuccess(data));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getPatientInvoiceDetailFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getMedicalHistory(action) {
  try {
     yield put(getMedicalHistoryRequest()); 
    const apiResponse = yield call(getMedicalHistoryAPI, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const listData = apiResponse.response.data[0];
        yield put(getMedicalHistorySuccess(listData));
        // Update individual state properties for both patient and doctor views
        yield put(getMedicationSuccess(listData.medicationData || []));
        yield put(getHealthConditionsSuccess(listData.healthconditionData || []));
        yield put(getSurgurySuccess(listData.surgeryData || []));
        yield put(getAllergySuccess(listData.allergieData || []));
        yield put(getVaccinationSuccess(listData.vaccinationData || []));
      } else {
        toast.error(apiResponse.response.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
        yield put(getMedicalHistoryFailure(apiResponse.response.message))
      }
    }
  } catch (error) { }
}

function* getPatientEprescription(action) {
  try {
    yield put(getPatientEprescriptionRequest());
    const apiResponse = yield call(getPatientPrescriptionApi, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        yield put(getPatientEprescriptionSuccess(data));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getPatientEprescriptionFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* getPatientMedicalReport(action) {
  try {
    yield put(getPatientMedicalReportRequest());
    const apiResponse = yield call(getMedicalReport, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        yield put(getPatientMedicalReportSuccess(data));
      } else {
        toast.error(apiResponse.response.message, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 1000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(getPatientMedicalReportFailure(apiResponse.response.message));
      }
    }
  } catch (error) {
    console.log("Error:", error);
  }
}

function* deleteHealthParamsSaga(action) {
  try {
    const apiResponse = yield call(deleteHealthParamsAPI, action.payload);

    if (Object.values(apiResponse).length === 0) {
      // Handle empty response if needed
    } else {
      if (apiResponse.response_type === "success") {
        const responseData = apiResponse.response.data;

        // Show success message
        toast.success(`Successfully deleted ${action.payload.type} records`, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 2000,
          style: { width: "auto", borderRadius: 8 },
        });

        yield put(deleteHealthParamsSuccess(responseData));

        // Invalidate cache for this specific health parameter type
        yield put({
          type: 'INVALIDATE_HEALTH_PARAM_CACHE',
          payload: {
            paramType: action.payload.type,
            duration: action.payload.duration,
            date: action.payload.date
          }
        });

        // Auto-refresh the data after successful deletion
        const refreshPayload = {
          token: action.payload.token,
          duration: action.payload.duration,
          date: action.payload.date
        };

        // Add user-specific IDs if needed
        if (action.payload.appointmentId) {
          refreshPayload.appointmentId = action.payload.appointmentId;
        }
        if (action.payload.patientId) {
          refreshPayload.patientId = action.payload.patientId;
        }

        // Dispatch refresh action based on deleted type
        const typeToActionMap = {
          'blood-sugar': bloodSugar,
          'blood-pressure': bloodPressure,
          'body-temperature': bodyTemp,
          'heart-rate': heartRate,
          'respiratory-rate': respiratoryRate,
          'spo2': bloodOxygen
        };

        const refreshAction = typeToActionMap[action.payload.type];
        if (refreshAction) {
          yield put(refreshAction(refreshPayload));
        }

      } else {
        toast.error(apiResponse.response.message || 'Failed to delete data', {
          position: "bottom-center",
          closeButton: false,
          autoClose: 3000,
          style: { width: "auto", borderRadius: 8 },
        });
        yield put(deleteHealthParamsFailure(apiResponse.response.error));
      }
    }
  } catch (error) {
    console.error("Delete Health Params Error:", error);
    toast.error('An error occurred while deleting data', {
      position: "bottom-center",
      closeButton: false,
      autoClose: 3000,
      style: { width: "auto", borderRadius: 8 },
    });
    yield put(deleteHealthParamsFailure(error.message));
  }
}

export function* userSagaWithCache() {
  yield takeLatest(bloodSugar.type, bloodSugarSaga);
  yield takeLatest(bloodPressure.type, bloodPressureSaga);
  yield takeLatest(healthParams.type, healthParamsSaga);
  yield takeLatest(bloodOxygen.type, bloodOxygenSaga);
  yield takeLatest(bodyTemp.type, bodyTempSaga);
  yield takeLatest(heartRate.type, heartRateSaga);
  yield takeLatest(respiratoryRate.type, respiratoryRateSaga);
  yield takeLatest(lipidProfile.type, lipidProfileSaga);
  yield takeLatest(patientProfile.type, getProfileSaga);
  yield takeLatest(healthConditions.type, getHealthConditions);
  yield takeLatest(medications.type, getMedications);
  yield takeLatest(surguries.type, getSurguries);
  yield takeLatest(allergies.type, getAllergies);
  yield takeLatest(vaccination.type, getVaccination);
  yield takeLatest(qrCodeAction.type, getQRcode);
  yield takeLatest(patientInvoiceListAction.type, getInvoiceList);
  yield takeLatest(patientInvoiceDetail.type, getPatientInvoiceDetail);
  yield takeLatest(getPatientMedicalReportAction.type, getPatientMedicalReport);
  yield takeLatest(getPatientEprescriptionAction.type, getPatientEprescription);
  yield takeLatest(getMedicalHistoryAction.type, getMedicalHistory);
  yield takeLatest(deleteHealthParams.type, deleteHealthParamsSaga);
  yield takeLatest(getStepsData.type, stepsDataSaga);
  yield takeLatest(getSleepData.type, sleepDataSaga);


}
